import useSWR from "swr"

import * as ForecastIO from "../api/forecast_io_api"

interface SuggestedPriceCaptionProps {
  /** The forecast customer as typed on the grid; the suggestion falls back to list price without one. */
  customer: string | null
  /** The catalog product the SKU picker resolved; nothing is suggested for a free-typed SKU. */
  product_id: string | null | undefined
  /** The price currently in the field, so the caption can offer to apply a different one. */
  current_price: number
  on_apply: (price: number) => void
  className?: string
}

/** Decide what the caption says for a suggestion, given the field's current value. */
export function suggested_price_state(
  suggestion: ForecastIO.SuggestedPrice | undefined,
  current_price: number
): { price: number; text: string; differs: boolean } | null {
  if (!suggestion || suggestion.price_per_case == null || !suggestion.source) return null
  const price = Math.round(Number(suggestion.price_per_case) * 100) / 100
  if (!Number.isFinite(price) || price <= 0) return null
  const customer = suggestion.customer_name ? ` (${suggestion.customer_name})` : ""
  let where: string
  if (suggestion.source === "customer price level") {
    where = `from NetSuite price level ${suggestion.label ?? ""}`.trim() + customer
  } else if (suggestion.source === "price list") {
    where = `from the imported price list${customer}`
  } else {
    where = "from the item's list price"
  }
  return { price, text: `${case_arithmetic(suggestion)}${where}`, differs: Math.round(current_price * 100) !== Math.round(price * 100) }
}

/** `($1.84 × 90) ` when the listed price was multiplied up to a case, else nothing. */
function case_arithmetic(suggestion: ForecastIO.SuggestedPrice): string {
  const listed = Number(suggestion.price_as_listed)
  const multiplier = Number(suggestion.per_case_multiplier)
  if (!Number.isFinite(listed) || !Number.isFinite(multiplier) || multiplier <= 1 || suggestion.price_as_listed == null) return ""
  const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 4 }).format(listed)
  return `(${money} × ${multiplier}) `
}

/**
 * Under the add-source dialog's "Price per case" field: NetSuite's price for this customer ×
 * item (customer price level first, item list price otherwise), with one click to take it.
 * The field stays editable — this only suggests.
 */
export function SuggestedPriceCaption(props: SuggestedPriceCaptionProps) {
  const { customer, product_id, current_price, on_apply, className } = props
  const { data } = useSWR(
    product_id ? ["forecast-suggested-price", customer ?? "", product_id] : null,
    () => ForecastIO.get_suggested_price(customer, product_id!)
  )
  const state = suggested_price_state(data, current_price)
  if (!state) return null
  return (
    <p className={`text-[11px] text-gray-500 ${className ?? ""}`}>
      {state.differs ? (
        <>
          Suggested {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(state.price)} a case {state.text}.{" "}
          <button type="button" className="font-medium text-blue-700 underline-offset-2 hover:underline" onClick={() => on_apply(state.price)}>
            Use it
          </button>
        </>
      ) : (
        <>Case price {state.text}.</>
      )}
    </p>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("suggested_price_state", () => {
    it("names the price level and offers the price when the field differs", () => {
      const state = suggested_price_state(
        { price_per_case: "26.70", source: "customer price level", label: "DSD", customer_name: "889 UNFI" },
        24
      )
      expect(state).toEqual({ price: 26.7, text: "from NetSuite price level DSD (889 UNFI)", differs: true })
    })
    it("says where the price came from without offering it when the field already has it", () => {
      const state = suggested_price_state({ price_per_case: "30", source: "item list price", label: "List price", customer_name: null }, 30)
      expect(state).toEqual({ price: 30, text: "from the item's list price", differs: false })
    })
    it("says a price list's per-stick price was multiplied up to a case, and never calls it NetSuite", () => {
      const state = suggested_price_state(
        {
          price_per_case: "165.60",
          source: "price list",
          label: "Customer price",
          customer_name: "Walmart",
          price_as_listed: "1.84",
          per_case_multiplier: "90",
        },
        0
      )
      expect(state).toEqual({ price: 165.6, text: "($1.84 × 90) from the imported price list (Walmart)", differs: true })
      expect(state?.text).not.toContain("NetSuite")
    })
    it("shows nothing for an unknown or zero price", () => {
      expect(suggested_price_state({ price_per_case: null, source: null, label: null, customer_name: null }, 0)).toBeNull()
      expect(suggested_price_state({ price_per_case: "0", source: "item list price", label: null, customer_name: null }, 0)).toBeNull()
      expect(suggested_price_state(undefined, 0)).toBeNull()
    })
  })
}
